import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { Dialog, IconButton, Switch, Tooltip } from "@mui/material";
import Button from "components/common/Button";
import Input from "components/common/Input";
import OrderCard from "components/common/OrderCard";
import OrderRow from "components/common/OrderRow";
import api from "config/api";
import { addDays, format, subMonths } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getModifierDisplayParts,
  getModifierOptionGroupLabel,
  getModifierRegion,
  isPizzaOrderDetail,
} from "utils/orderDisplay";
import { OrderItem } from "utils/types";

const NEW_ORDER_BLINK_MS = 2 * 60 * 1000;
const AUTO_REFRESH_STORAGE_KEY = "adminOrdersAutoRefresh";
const CHECKED_ORDER_IDS_STORAGE_KEY = "adminOrdersCheckedOrderIds";

const loadStoredOrderIds = (key: string) => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || "[]");
    return new Set(
      Array.isArray(parsed)
        ? parsed.map((id) => Number(id || 0)).filter((id) => id > 0)
        : [],
    );
  } catch {
    return new Set<number>();
  }
};

const AllOrders = () => {
  const [mode, setMode] = useState("ROW");
  const [data, setData] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [taxData, setTaxData] = useState<any>({});
  const [companyData, setCompanyData] = useState<any>(null);
  const [selectedOrder, setSelectedOrder] = useState<OrderItem | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(
    () => window.localStorage.getItem(AUTO_REFRESH_STORAGE_KEY) === "1",
  );
  const [newOrderExpiresAt, setNewOrderExpiresAt] = useState<Record<number, number>>({});
  const [checkedOrderIds, setCheckedOrderIds] = useState<Set<number>>(
    () => loadStoredOrderIds(CHECKED_ORDER_IDS_STORAGE_KEY),
  );
  const [dates, setDates] = useState({
    fromDate: format(subMonths(new Date(), 1), "yyyy-MM-dd"),
    toDate: format(new Date(), "yyyy-MM-dd"),
  });
  const [timeZone, setTimeZone] = useState("Pacific Standard Time");
  const knownOrderIdsRef = useRef<Set<number>>(new Set());
  const checkedOrderIdsRef = useRef<Set<number>>(checkedOrderIds);

  const getOrderCreatedTimestamp = useCallback((item: OrderItem) => {
    const rawOrderDateTime = String((item as any)?.onDateTime || "").trim();
    const parsedOrderDateTime = rawOrderDateTime ? new Date(rawOrderDateTime) : null;

    if (parsedOrderDateTime && !Number.isNaN(parsedOrderDateTime.getTime())) {
      return parsedOrderDateTime.getTime();
    }

    const rawDate = String(item.onDate || "").trim();
    const rawTime = String(item.onTime || "").trim();
    const parsedScheduleDateTime = new Date([rawDate, rawTime].filter(Boolean).join(" "));

    return Number.isNaN(parsedScheduleDateTime.getTime()) ? 0 : parsedScheduleDateTime.getTime();
  }, []);

  const fetchData = useCallback((options?: { markNew?: boolean; silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    return api
      .get(
        `/api/OrderSubmition/GetSearchOrders?fromDate=${format(
          new Date(dates.fromDate),
          "MM/d/yyyy",
        )}&toDate=${format(addDays(new Date(dates.toDate), 1), "MM/d/yyyy")}`,
      )
      .then(({ data }) => {
        const orders = Array.isArray(data) ? data : [];
        const incomingIds = orders
          .map((item: OrderItem) => Number(item.orderID || 0))
          .filter((id: number) => id > 0);

        if (options?.markNew) {
          const now = Date.now();
          const newOrderEntries = orders
            .map((item: OrderItem) => {
              const id = Number(item.orderID || 0);
              const orderTimestamp = getOrderCreatedTimestamp(item);
              const expiresAt = orderTimestamp ? orderTimestamp + NEW_ORDER_BLINK_MS : 0;

              return { id, expiresAt };
            })
            .filter(
              ({ id, expiresAt }: { id: number; expiresAt: number }) =>
                id > 0 &&
                !knownOrderIdsRef.current.has(id) &&
                !checkedOrderIdsRef.current.has(id) &&
                expiresAt > now,
            );

          if (newOrderEntries.length) {
            setNewOrderExpiresAt((prev) => ({
              ...prev,
              ...Object.fromEntries(
                newOrderEntries.map(({ id, expiresAt }: { id: number; expiresAt: number }) => [
                  id,
                  expiresAt,
                ]),
              ),
            }));
          }
        }

        knownOrderIdsRef.current = new Set(incomingIds);
        setData(orders);
      })
      .catch((e) => {
        console.error("ERROR :", e);
      })
      .finally(() => {
        if (!options?.silent) setLoading(false);
      });
  }, [dates.fromDate, dates.toDate, getOrderCreatedTimestamp]);

  useEffect(() => {
    fetchData({ markNew: true });
    api
      .get("/api/initialData")
      .then(({ data }) => {
        if (data?.taxes) setTaxData(data.taxes);
        if (data?.companyData) setCompanyData(data.companyData);
        if (data?.companyData?.timeZone) setTimeZone(data.companyData.timeZone);
      })
      .catch((e) => {
        console.error("Failed to load /api/initialData for admin orders", e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.localStorage.setItem(AUTO_REFRESH_STORAGE_KEY, autoRefresh ? "1" : "0");
  }, [autoRefresh]);

  useEffect(() => {
    checkedOrderIdsRef.current = checkedOrderIds;
    window.localStorage.setItem(
      CHECKED_ORDER_IDS_STORAGE_KEY,
      JSON.stringify(Array.from(checkedOrderIds)),
    );
  }, [checkedOrderIds]);

  useEffect(() => {
    if (!autoRefresh) return;

    const timer = window.setInterval(() => {
      fetchData({ markNew: true, silent: true });
    }, 60 * 1000);

    return () => window.clearInterval(timer);
  }, [autoRefresh, fetchData]);

  useEffect(() => {
    const expiresAtValues = Object.values(newOrderExpiresAt);
    if (!expiresAtValues.length) return;

    const nextExpiresAt = Math.min(...expiresAtValues);
    const timer = window.setTimeout(() => {
      const now = Date.now();
      setNewOrderExpiresAt((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(([, expiresAt]) => Number(expiresAt) > now),
        ),
      );
    }, Math.max(nextExpiresAt - Date.now(), 0) + 50);

    return () => window.clearTimeout(timer);
  }, [newOrderExpiresAt]);

  const isOrderBlinking = (item: OrderItem) =>
    Number(newOrderExpiresAt[Number(item.orderID || 0)] || 0) > Date.now();

  const openOrderDetails = (item: OrderItem) => {
    const orderId = Number(item.orderID || 0);

    if (orderId > 0) {
      setCheckedOrderIds((prev) => {
        const next = new Set(prev);
        next.add(orderId);
        return next;
      });
      setNewOrderExpiresAt((prev) => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
    }

    setSelectedOrder(item);
  };

  const getCustomerName = (item: OrderItem) =>
    [
      (item as any)?.customer?.firstName ?? (item as any)?.firstName,
      (item as any)?.customer?.lastName ?? (item as any)?.lastName,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(" ");

  const firstText = (...values: any[]) =>
    values.map((value) => String(value || "").trim()).find(Boolean) || "";

  const getOrderAddressType = (item: any) =>
    firstText(item?.AddressType, item?.addressType, item?.customer?.addressType);

  const getOrderAddressParts = (item: OrderItem) => {
    const isTakeOut = String((item as any)?.orderType || "")
      .toLowerCase()
      .includes("take");

    if (isTakeOut) {
      return {
        address: firstText(
          [companyData?.address1, companyData?.address2]
            .map((value) => String(value || "").trim())
            .filter(Boolean)
            .join(", "),
          companyData?.address1,
          companyData?.address2,
        ),
        place: "Pickup",
      };
    }

    const address = firstText(
      (item as any)?.address,
      (item as any)?.address1,
      (item as any)?.customer?.address,
      (item as any)?.customer?.address1,
    );
    const addressType = getOrderAddressType(item);
    const addressDetails = [
      address,
      (item as any)?.suitNo ? `Suite ${String((item as any).suitNo).trim()}` : "",
      (item as any)?.roomNo ? `Room ${String((item as any).roomNo).trim()}` : "",
      (item as any)?.buzzerNo ? `Buzzer ${String((item as any).buzzerNo).trim()}` : "",
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(", ");

    return { address: addressDetails, place: addressType };
  };

  const renderPlaceChip = (place: string) => {
    const normalized = place.trim().toLowerCase();
    const Icon = normalized === "home" ? HomeOutlinedIcon : normalized === "office" ? WorkOutlineOutlinedIcon : PlaceOutlinedIcon;

    if (!place) return null;

    return (
      <span className="inline-flex w-fit shrink-0 items-center gap-1 rounded border border-slate-200 bg-white px-2 py-0.5 text-xs font-semibold text-slate-700 shadow-sm">
        <Icon className="!text-[13px] text-primary" />
        {place}
      </span>
    );
  };

  const getDetailModifierGroups = (detail: any) => {
    const buildOptionGroups = (modifiers: any[], optionDetail: any) => {
      const grouped = modifiers.reduce((groups: Record<string, any[]>, modifier: any) => {
        const label = getModifierOptionGroupLabel(modifier);
        groups[label] = [...(groups[label] || []), modifier];
        return groups;
      }, {});

      return Object.entries(grouped).map(([title, groupedModifiers]) => ({
        title,
        detail: optionDetail,
        modifiers: groupedModifiers,
        showPizzaControls: false,
      }));
    };

    if (Array.isArray(detail?.modifiers) && detail.modifiers.length) {
      if (isPizzaOrderDetail(detail)) {
        const toppingModifiers = detail.modifiers.filter(
          (modifier: any) => getModifierRegion(modifier) > 0,
        );
        const optionModifiers = detail.modifiers.filter(
          (modifier: any) => getModifierRegion(modifier) === 0,
        );

        return [
          {
            title: "Pizza toppings",
            detail,
            modifiers: toppingModifiers,
            showPizzaControls: true,
          },
          ...buildOptionGroups(optionModifiers, detail),
        ].filter((group) => group.modifiers.length);
      }

      return buildOptionGroups(detail.modifiers, detail);
    }

    if (Array.isArray(detail?.data?.selectedDealPizzas)) {
      return detail.data.selectedDealPizzas
        .map((dealPizza: any, index: number) => ({
          title: dealPizza?.pizzaName || dealPizza?.fullDescription || `Pizza ${index + 1}`,
          detail: dealPizza,
          modifiers: Array.isArray(dealPizza?.modifiers) ? dealPizza.modifiers : [],
          showPizzaControls: true,
        }))
        .filter((group: any) => group.modifiers.length);
    }

    return [];
  };

  const renderModifierChips = (
    detail: any,
    modifiers: any[] = [],
    showPizzaControls = isPizzaOrderDetail(detail),
  ) => {
    if (!modifiers.length) return null;
    const isPizza = isPizzaOrderDetail(detail);

    return (
      <div className="flex flex-wrap gap-1.5">
        {modifiers.map((modifier: any, modifierIndex: number) => {
          const parts = getModifierDisplayParts(
            showPizzaControls ? detail : { ...detail, isPizzaItem: false, pizzaSize: null, pizzaCrust: null },
            modifier,
          );
          if (!parts.name) return null;
          const price = Number(
            modifier?.totalValue || modifier?.subTotal || modifier?.unitPrice || 0,
          );

          return (
            <span
              key={`${parts.name}-${modifierIndex}`}
              className={`inline-flex max-w-full flex-wrap items-center gap-1 rounded border px-2 py-1 text-[11px] font-semibold leading-4 ${
                isPizza
                  ? "border-blue-100 bg-blue-50 text-slate-900"
                  : "border-slate-200 bg-slate-50 text-slate-800"
              }`}
            >
              {!!parts.sideLabel && (
                <span className="rounded bg-white px-1 py-0.5 text-[9px] font-bold uppercase text-blue-700 ring-1 ring-blue-100">
                  {parts.sideLabel}
                </span>
              )}
              {!!parts.multiplierLabel && (
                <span className="rounded bg-slate-950 px-1 py-0.5 text-[9px] font-bold uppercase text-white">
                  {parts.multiplierLabel}
                </span>
              )}
              <span className="break-words">{parts.name}</span>
              {price > 0 && (
                <span className="rounded bg-white/80 px-1.5 py-0.5 text-[9px] font-bold text-slate-600 ring-1 ring-slate-200">
                  {formatMoney(price)}
                </span>
              )}
            </span>
          );
        })}
      </div>
    );
  };

  const getDetailPreferenceText = (detail: any) => {
    const raw = String(detail?.description || "").trim();
    if (!raw) return "";

    const hasStructuredModifiers = getDetailModifierGroups(detail).length > 0;
    const productLabel = String(detail?.productCode || "").trim().toUpperCase();
    const parts = raw
      .replace(/\\r\\n/g, "\n")
      .replace(/\\n/g, "\n")
      .split(/\n|\||\*/)
      .map((line) => line.trim())
      .filter(Boolean)
      .filter((line, index) => !(index === 0 && productLabel && line.toUpperCase() === productLabel));

    const visibleParts = hasStructuredModifiers
      ? parts.filter((line) => /^(crust|size|spice)\s*:/i.test(line))
      : parts;

    return visibleParts.join(" | ");
  };

  const getDetailNote = (detail: any) =>
    firstText(detail?.note, detail?.notes, detail?.specialInstruction, detail?.specialInstructions);

  const getDetailTaxTotal = (detail: any) =>
    Number(detail?.tax1 || 0) + Number(detail?.tax2 || 0) + Number(detail?.tax3 || 0);

  const formatMoney = (value: any) => `CA$${Number(value || 0).toFixed(2)}`;

  const getOrderDiscountAmount = (item: any = {}) => {
    const lineDiscountAmount = Array.isArray(item?.orderDetail)
      ? item.orderDetail.reduce(
          (sum: number, detail: any) =>
            sum +
            Number(
              detail?.discount ||
                detail?.discAmt ||
                detail?.promoDiscount ||
                detail?.discountAmount ||
                0,
            ),
          0,
        )
      : 0;

    return (
      [
        item?.discount,
        item?.promoDiscount,
        item?.promoDiscAmt,
        item?.discAmt,
        item?.discountAmount,
        lineDiscountAmount,
      ]
        .map((value) => Number(value || 0))
        .find((value) => value > 0) || 0
    );
  };

  const getOrderSummaryTotal = (item: any = {}) => {
    const apiTotal = Number(item?.orderTotal || 0);
    const discount = getOrderDiscountAmount(item);
    const expectedTotal =
      Number(item?.subTotal || 0) +
      Number(item?.totalTax1 || 0) +
      Number(item?.totalTax2 || 0) +
      Number(item?.totalTax3 || 0) +
      Number(item?.deliveryCharge || 0) +
      Number(item?.tip || 0) -
      discount;

    if (discount > 0 && Math.abs(apiTotal - expectedTotal) > 0.009) {
      return expectedTotal;
    }

    return apiTotal;
  };

  const getTaxLabel = (index: number, fallback: string) =>
    taxData?.[index]?.name || taxData?.[index]?.taxName || fallback;

  const toDisplayDateTime = (dateValue: any, timeValue?: any) => {
    const dateText = String(dateValue || "").trim();
    const timeText = String(timeValue || "").trim();

    if (!dateText && !timeText) return "--";

    const combined = timeText ? `${dateText} ${timeText}` : dateText;
    const parsed = new Date(timeText && /^\d{4}-\d{2}-\d{2}$/.test(dateText)
      ? `${dateText}T${timeText}`
      : combined);

    if (!Number.isNaN(parsed.getTime())) {
      return format(parsed, "MM/dd/yyyy h:mm aa");
    }

    return [dateText, timeText].filter(Boolean).join(" at ");
  };

  const getRequestedDateTime = (item: OrderItem) => toDisplayDateTime(item.onDate, item.onTime);

  const getListOrderDate = (item: OrderItem) => {
    const rawOrderDateTime = String((item as any)?.onDateTime || "").trim();
    const parsed = rawOrderDateTime ? new Date(rawOrderDateTime) : null;

    if (parsed && !Number.isNaN(parsed.getTime())) {
      return format(parsed, "MM/dd/yyyy");
    }

    return String(item.onDate || "Unknown Date");
  };

  const getListOrderTime = (item: OrderItem) => {
    const rawOrderDateTime = String((item as any)?.onDateTime || "").trim();
    const parsed = rawOrderDateTime ? new Date(rawOrderDateTime) : null;

    if (parsed && !Number.isNaN(parsed.getTime())) {
      return format(parsed, "HH:mm:ss");
    }

    return String(item.onTime || "--");
  };

  const getScheduledOrderDate = (item: OrderItem) => {
    const rawDate = String(item.onDate || "").trim();
    const parsed = rawDate ? new Date(rawDate) : null;

    if (parsed && !Number.isNaN(parsed.getTime())) {
      return format(parsed, "MM/dd/yyyy");
    }

    return rawDate || "--";
  };

  const isDeliveryOrder = (item: OrderItem) =>
    String(item.orderType || "").toLowerCase().includes("delivery");

  const getListOrderTimestamp = (item: OrderItem) => {
    const rawOrderDateTime = String((item as any)?.onDateTime || "").trim();
    const parsedOrderDateTime = rawOrderDateTime ? new Date(rawOrderDateTime) : null;

    if (parsedOrderDateTime && !Number.isNaN(parsedOrderDateTime.getTime())) {
      return parsedOrderDateTime.getTime();
    }

    const rawDate = String(item.onDate || "").trim();
    const rawTime = String(item.onTime || "").trim();
    const parsedScheduleDateTime = new Date([rawDate, rawTime].filter(Boolean).join(" "));

    return Number.isNaN(parsedScheduleDateTime.getTime()) ? 0 : parsedScheduleDateTime.getTime();
  };

  const filteredOrders = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return data;

    return data.filter((item) => {
      const values = [
        item.orderID,
        item.orderNumber,
        item.tokenNumber,
        item.orderType,
        item.paymentType,
        item.onDateTime,
        getListOrderDate(item),
        getListOrderTime(item),
        item.onDate,
        item.onTime,
        item.orderTotal,
        item.customerID,
        getCustomerName(item),
      ];

      return values
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(query));
    });
  }, [data, searchText]);

  const groupedOrders = useMemo(() => {
    const groups = filteredOrders.reduce((orderGroups, item) => {
      const groupDate = getListOrderDate(item);
      const existing = orderGroups.find((group) => group.date === groupDate);

      if (existing) {
        existing.orders.push(item);
      } else {
        orderGroups.push({ date: groupDate, orders: [item] });
      }

      return orderGroups;
    }, [] as { date: string; orders: OrderItem[] }[]);

    return groups
      .map((group) => {
        const sameDayCount = group.orders.filter(
          (item) => getScheduledOrderDate(item) === getListOrderDate(item),
        ).length;
        const futureDayCount = group.orders.length - sameDayCount;

        return {
          ...group,
          sameDayCount,
          futureDayCount,
          orders: [...group.orders].sort(
          (a, b) =>
            getListOrderTimestamp(b) - getListOrderTimestamp(a) ||
            Number(b.orderID || 0) - Number(a.orderID || 0),
          ),
        };
      })
      .sort((a, b) => getListOrderTimestamp(b.orders[0]) - getListOrderTimestamp(a.orders[0]));
  }, [filteredOrders]);

  const todayScheduledDeliveryOrders = useMemo(() => {
    const today = format(new Date(), "MM/dd/yyyy");

    return data
      // Show only advance delivery orders due today. Same-day delivery orders stay in the main list only.
      .filter(
        (item) =>
          isDeliveryOrder(item) &&
          getScheduledOrderDate(item) === today &&
          getListOrderDate(item) !== getScheduledOrderDate(item),
      )
      .sort(
        (a, b) =>
          getListOrderTimestamp(a) - getListOrderTimestamp(b) ||
          Number(a.orderID || 0) - Number(b.orderID || 0),
      );
  }, [data]);

  const shouldAnimateTodayDeliveryOrders = todayScheduledDeliveryOrders.length > 3;
  const renderTodayScheduledDeliveryOrderButtons = (isDecorative = false) => (
    <span className="inline-flex items-center gap-2">
      {todayScheduledDeliveryOrders.map((item) => (
        <button
          key={item.orderID}
          type="button"
          className="inline-flex h-6 items-center rounded-md border border-rose-200 bg-white px-2 text-sm font-bold leading-none text-slate-900 shadow-sm transition hover:border-rose-300 hover:bg-rose-100 focus:outline-none focus:ring-2 focus:ring-rose-300"
          onClick={() => openOrderDetails(item)}
          tabIndex={isDecorative ? -1 : 0}
          aria-hidden={isDecorative}
        >
          #{item.orderID}
        </button>
      ))}
    </span>
  );

  const renderGroupHeader = (
    group: { date: string; orders: OrderItem[]; sameDayCount: number; futureDayCount: number },
    topClass: string,
  ) => {
    const hasFutureOrders = group.futureDayCount > 0;

    return (
      <div
        className={`sticky ${topClass} z-20 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-2 text-sm font-semibold shadow-sm ${
          hasFutureOrders
            ? "border-rose-200 bg-gradient-to-r from-rose-50 via-white to-orange-50 text-rose-950"
            : "border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-sky-50 text-slate-950"
        }`}
      >
        <span className="text-[15px] font-semibold">{group.date}</span>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-sm">
            Total: {group.orders.length}
          </span>
          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 shadow-sm">
            Same day: {group.sameDayCount}
          </span>
          <span
            className={`rounded-full border px-2.5 py-1 text-xs font-semibold shadow-sm ${
              hasFutureOrders
                ? "border-rose-200 bg-rose-50 text-rose-700"
                : "border-slate-200 bg-white text-slate-500"
            }`}
          >
            Future day: {group.futureDayCount}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full w-full min-w-0 flex-col overflow-hidden p-4">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-gray-100 px-5 py-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-themeSelColor">
              <ReceiptLongRoundedIcon className="!text-[24px]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                All Orders
              </h3>
              <p className="text-sm text-gray-500">
                {loading
                  ? "Loading orders..."
                  : `${filteredOrders.length} of ${data.length} orders shown`}
              </p>
            </div>
          </div>

          <div className="min-w-0 flex-1 xl:max-w-[420px]">
            <style>{`
              @keyframes today-delivery-orders-marquee {
                0% { transform: translateX(0); }
                100% { transform: translateX(-50%); }
              }
              @keyframes newOrderBlink {
                0%, 100% {
                  border-color: #f43f5e;
                  box-shadow:
                    inset 4px 0 0 #f43f5e,
                    0 0 0 1px rgba(244, 63, 94, 0.45),
                    0 8px 22px rgba(244, 63, 94, 0.1);
                }
                50% {
                  border-color: #fecdd3;
                  box-shadow:
                    inset 4px 0 0 #fecdd3,
                    0 0 0 5px rgba(244, 63, 94, 0.4),
                    0 16px 34px rgba(244, 63, 94, 0.22);
                }
              }
              .dcs-new-order-blink {
                animation: newOrderBlink 1s ease-in-out infinite;
              }
            `}</style>
            <div className="overflow-hidden rounded-lg border border-rose-100 bg-rose-50 px-3 py-2">
              <div className="mb-1 flex items-center justify-between gap-3">
                <Tooltip
                  title="Shows delivery orders due today, only when the placed order date is different from the delivery date."
                  arrow
                >
                  <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-rose-700">
                    Today scheduled delivery
                  </span>
                </Tooltip>
                <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-bold text-rose-700 shadow-sm">
                  {todayScheduledDeliveryOrders.length}
                </span>
              </div>
              {todayScheduledDeliveryOrders.length ? (
                <div className="relative h-7 overflow-hidden text-sm font-bold text-slate-900">
                  {shouldAnimateTodayDeliveryOrders ? (
                    <div className="absolute flex min-w-max items-center gap-8 whitespace-nowrap [animation:today-delivery-orders-marquee_16s_linear_infinite]">
                      {renderTodayScheduledDeliveryOrderButtons()}
                      {renderTodayScheduledDeliveryOrderButtons(true)}
                    </div>
                  ) : (
                    <div className="truncate whitespace-nowrap">
                      {renderTodayScheduledDeliveryOrderButtons()}
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-7 text-sm font-semibold text-slate-500">
                  No delivery orders scheduled today
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <Tooltip title="When on, this page refreshes orders every 1 minute. New orders blink for 1 minute." arrow>
              <label className="flex min-w-[126px] flex-col gap-1 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                Auto refresh
                <div className="flex h-10 items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-2">
                  <span className={`text-xs font-bold ${autoRefresh ? "text-emerald-600" : "text-gray-500"}`}>
                    {autoRefresh ? "ON" : "OFF"}
                  </span>
                  <Switch
                    size="small"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    inputProps={{ "aria-label": "Auto refresh orders" }}
                  />
                </div>
              </label>
            </Tooltip>

            <label className="flex min-w-[240px] flex-col gap-1 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
              Search
              <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-1 transition focus-within:border-primary focus-within:bg-white">
                <SearchRoundedIcon className="mr-1 !text-[18px] text-gray-400" />
                <Input
                  value={searchText}
                  size="small"
                  placeholder="Order, customer, payment..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "transparent",
                      fontSize: 13,
                      height: 30,
                    },
                    "& fieldset": { border: 0 },
                    "& input": { padding: "4px 2px" },
                  }}
                  onValueChange={setSearchText}
                />
              </div>
            </label>

            <div className="flex w-fit rounded-lg bg-gray-100 p-1">
              <button
                type="button"
                className={`flex h-9 w-10 items-center justify-center rounded-md transition ${
                  mode === "ROW"
                    ? "bg-primary text-themeSelColor shadow-sm"
                    : "text-gray-600 hover:text-gray-950"
                }`}
                onClick={() => setMode("ROW")}
                aria-label="Show row view"
              >
                <TableRowsRoundedIcon />
              </button>
              <button
                type="button"
                className={`flex h-9 w-10 items-center justify-center rounded-md transition ${
                  mode === "GRID"
                    ? "bg-primary text-themeSelColor shadow-sm"
                    : "text-gray-600 hover:text-gray-950"
                }`}
                onClick={() => setMode("GRID")}
                aria-label="Show card view"
              >
                <GridViewRoundedIcon />
              </button>
            </div>

            <div className="flex items-end gap-2 max-sm:grid max-sm:grid-cols-2">
              <label className="flex min-w-[150px] flex-col gap-1 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                From
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 transition focus-within:border-primary focus-within:bg-white">
                  <Input
                    value={dates.fromDate}
                    size="small"
                    type="date"
                    placeholder="From"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "transparent",
                        fontSize: 13,
                        height: 30,
                      },
                      "& fieldset": { border: 0 },
                      "& input": { padding: "4px 2px" },
                    }}
                    onValueChange={(fromDate) =>
                      setDates((e) => ({ ...e, fromDate }))
                    }
                  />
                </div>
              </label>
              <label className="flex min-w-[150px] flex-col gap-1 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                To
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 transition focus-within:border-primary focus-within:bg-white">
                  <Input
                    value={dates.toDate}
                    size="small"
                    type="date"
                    placeholder="To"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "transparent",
                        fontSize: 13,
                        height: 30,
                      },
                      "& fieldset": { border: 0 },
                      "& input": { padding: "4px 2px" },
                    }}
                    onValueChange={(toDate) =>
                      setDates((e) => ({ ...e, toDate }))
                    }
                  />
                </div>
              </label>
              <div className="max-sm:col-span-2 max-sm:flex max-sm:justify-end">
                <Button disabled={loading} onClick={() => fetchData({ markNew: true })}>
                  <span className="inline-flex items-center gap-1.5">
                    <SearchRoundedIcon fontSize="small" />
                    Search
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="min-h-0 min-w-0 flex-1 overflow-auto bg-gray-50 p-4">
          {mode === "GRID" ? (
            <div className="flex min-w-0 flex-col gap-3">
              {groupedOrders.map((group) => (
                <div key={group.date} className="flex flex-col gap-2">
                  {renderGroupHeader(group, "top-0")}
                  {group.orders.map((item) => (
                    <OrderCard
                      key={item.orderID}
                      {...item}
                      timeZone={timeZone}
                      taxData={taxData}
                      isNew={isOrderBlinking(item)}
                      onDoubleClick={() => openOrderDetails(item)}
                    />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="min-w-[1300px]">
              <div className="sticky top-0 z-30 grid grid-cols-[90px_minmax(180px,1.2fr)_120px_130px_110px_210px_170px_120px] gap-4 rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-white shadow-sm">
                <b>Order</b>
                <b>Customer</b>
                <b>Type</b>
                <b>Date</b>
                <b>Time</b>
                <b>Delivery/Pickup</b>
                <b>Payment</b>
                <b className="text-right">Price</b>
              </div>
              <div className="mt-3 flex flex-col gap-2">
                {groupedOrders.map((group) => (
                  <div key={group.date} className="flex flex-col gap-2">
                    {renderGroupHeader(group, "top-[40px]")}
                    {group.orders.map((item) => (
                      <OrderRow
                        key={item.orderID}
                        {...item}
                        timeZone={timeZone}
                        isNew={isOrderBlinking(item)}
                        onDoubleClick={() => openOrderDetails(item)}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!loading && !filteredOrders.length && (
            <div className="flex min-h-[260px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white text-sm font-semibold text-gray-500">
              No orders found for this date range or search.
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            width: "min(1480px, calc(100vw - 20px))",
            maxHeight: "calc(100vh - 20px)",
            display: "flex",
            fontFamily:
              "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
        }}
      >
        {selectedOrder && (
          <div
            className="overflow-y-auto bg-white text-slate-900 [letter-spacing:0]"
            style={{ maxHeight: "calc(100vh - 20px)" }}
          >
            {(() => {
              const selectedDiscount = getOrderDiscountAmount(selectedOrder);
              const selectedTip = Number(selectedOrder.tip || 0);
              const selectedSummaryTotal = getOrderSummaryTotal(selectedOrder);
              const selectedAddressParts = getOrderAddressParts(selectedOrder);
              const hasTaxColumn = !!Number(selectedOrder.totalTax1 || 0);
              const productGridColumns = hasTaxColumn
                ? "minmax(280px, 1fr) 72px 120px 90px 120px"
                : "minmax(280px, 1fr) 72px 120px 120px";
              const customerName = getCustomerName(selectedOrder) || "Walk-in Customer";
              const isTakeOut = String(selectedOrder.orderType || "")
                .toLowerCase()
                .includes("take");
              const scheduleLabel = isTakeOut ? "Pickup" : "Delivery";
              const addressLabel = isTakeOut ? "Pickup Address" : "Delivery Address";
              const noteText = String(selectedOrder.notes || "").trim();

              return (
                <div className="p-3 sm:p-4">
                  <div className="flex flex-wrap items-center gap-2 border-b border-slate-200/70 px-1 pb-2">
                    <p className="mr-1 text-[13px] font-semibold text-slate-900 sm:text-[14px]">
                      Order ID : <span className="font-bold">{selectedOrder.orderID}</span>
                      {selectedOrder.orderNumber ? (
                        <span className="font-bold"> · {selectedOrder.orderNumber}</span>
                      ) : null}
                      {!!customerName && <span className="ml-2 font-bold">{customerName}</span>}
                    </p>
                    <IconButton
                      className="!ml-auto !h-8 !w-8"
                      onClick={() => setSelectedOrder(null)}
                      aria-label="Close order details"
                    >
                      <CloseRoundedIcon className="!text-[18px]" />
                    </IconButton>
                  </div>

                  <div className="grid grid-cols-1 items-start gap-2 px-1 pt-2 xl:grid-cols-[minmax(0,1fr)_245px]">
                    <div className="min-w-0">
                      <div className="mb-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                        <span
                          className={`rounded border px-2 py-0.5 font-semibold ${
                            isTakeOut
                              ? "border-amber-200 bg-amber-50 text-amber-700"
                              : "border-blue-200 bg-blue-50 text-blue-700"
                          }`}
                        >
                          Type: <span className="text-inherit">{selectedOrder.orderType || "--"}</span>
                        </span>
                        <span className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-semibold text-slate-700">
                          Date: <span className="text-slate-950">{getListOrderDate(selectedOrder)}</span>
                        </span>
                        <span className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-semibold text-slate-700">
                          Time: <span className="text-slate-950">{getListOrderTime(selectedOrder)}</span>
                        </span>
                        <span
                          className={`rounded border px-2 py-0.5 font-semibold ${
                            isTakeOut
                              ? "border-blue-200 bg-blue-50 text-blue-700"
                              : "border-blue-200 bg-blue-50 text-blue-700"
                          }`}
                        >
                          {scheduleLabel}:{" "}
                          <span className="text-inherit">{getRequestedDateTime(selectedOrder)}</span>
                        </span>
                        <span className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-semibold text-slate-700">
                          Payment:{" "}
                          <span className="text-slate-950">
                            {String(selectedOrder.paymentType || "").trim() || "Pay at Counter"}
                          </span>
                        </span>
                      </div>
                      <div className="rounded border border-rose-100 bg-rose-50/55 px-2 py-1">
                        <p className="text-[9px] font-semibold uppercase text-slate-500">Note</p>
                        <p className="break-words text-[12px] leading-5 text-slate-800 sm:text-[13px]">
                          {noteText || "--"}
                        </p>
                      </div>
                      <div className="mt-1.5 rounded border border-blue-100 bg-blue-50/70 px-2 py-1">
                        <p className="text-[9px] font-semibold uppercase text-slate-500">
                          {addressLabel}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          {renderPlaceChip(selectedAddressParts.place)}
                          <p className="min-w-0 break-words text-[12px] leading-5 text-slate-800 sm:text-[13px]">
                            {selectedAddressParts.address || "--"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="min-w-0 rounded border border-slate-200 bg-white px-2 py-1 shadow-sm">
                      <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 py-0.5">
                        <span className="text-[9px] font-semibold uppercase text-slate-500">
                          Sub Total
                        </span>
                        <span className="text-[12px] font-semibold leading-5 text-slate-900">
                          {formatMoney(selectedOrder.subTotal)}
                        </span>
                      </div>
                      {!!selectedDiscount && (
                        <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 py-0.5">
                          <span className="text-[9px] font-bold uppercase text-[#f10505]">
                            Special Discount
                          </span>
                          <span className="text-[12px] font-bold leading-5 text-[#f10505]">
                            -{formatMoney(selectedDiscount)}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 py-0.5">
                        <span className="text-[9px] font-semibold uppercase text-slate-500">
                          {getTaxLabel(0, "Tax 1")}
                        </span>
                        <span className="text-[12px] font-semibold leading-5 text-slate-900">
                          {formatMoney(selectedOrder.totalTax1)}
                        </span>
                      </div>
                      {!!Number(selectedOrder.totalTax2 || 0) && (
                        <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 py-0.5">
                          <span className="text-[9px] font-semibold uppercase text-slate-500">
                            {getTaxLabel(1, "Tax 2")}
                          </span>
                          <span className="text-[12px] font-semibold leading-5 text-slate-900">
                            {formatMoney(selectedOrder.totalTax2)}
                          </span>
                        </div>
                      )}
                      {!!Number(selectedOrder.totalTax3 || 0) && (
                        <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 py-0.5">
                          <span className="text-[9px] font-semibold uppercase text-slate-500">
                            {getTaxLabel(2, "Tax 3")}
                          </span>
                          <span className="text-[12px] font-semibold leading-5 text-slate-900">
                            {formatMoney(selectedOrder.totalTax3)}
                          </span>
                        </div>
                      )}
                      {!!Number(selectedOrder.deliveryCharge || 0) && (
                        <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 py-0.5">
                          <span className="text-[9px] font-semibold uppercase text-slate-500">
                            Delivery Charge
                          </span>
                          <span className="text-[12px] font-semibold leading-5 text-slate-900">
                            {formatMoney(selectedOrder.deliveryCharge)}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 py-0.5">
                        <span className="text-[9px] font-semibold uppercase text-slate-500">
                          Staff Tip
                        </span>
                        <span className="text-[12px] font-semibold leading-5 text-slate-900">
                          {formatMoney(selectedTip)}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-3 rounded bg-slate-950 px-2 py-1 text-white shadow-sm">
                        <span className="text-[9px] font-semibold uppercase text-white/70">
                          Order Total
                        </span>
                        <span className="text-[15px] font-extrabold leading-6 text-white">
                          {formatMoney(selectedSummaryTotal)}
                        </span>
                      </div>
                    </div>
                  </div>

                <div className="mt-3 overflow-x-auto rounded border border-gray-200 bg-white">
                  <p className="border-b border-gray-200 px-4 py-2 text-sm font-semibold text-slate-950">
                    Product Details
                  </p>
                  <div
                    className="grid gap-4 border-b border-gray-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-950"
                    style={{ gridTemplateColumns: productGridColumns }}
                  >
                    <span>Product</span>
                    <span className="text-center">Qty</span>
                    <span className="text-center">Note</span>
                    {hasTaxColumn && (
                      <span className="text-center">{getTaxLabel(0, "GST")}</span>
                    )}
                    <span className="text-right">Total</span>
                  </div>
                  <div className="divide-y divide-gray-100 bg-white">
                    {(selectedOrder.orderDetail || []).map((detail: any, index: number) => (
                      <div
                        key={`${detail?.productCode || "product"}-${index}`}
                        className="px-4 py-3 text-sm"
                      >
                        <div
                          className="grid gap-4"
                          style={{ gridTemplateColumns: productGridColumns }}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-bold leading-5 text-slate-950">
                              {detail?.productCode || detail?.description || `Item ${index + 1}`}
                            </p>
                            {!!getDetailPreferenceText(detail) && (
                              <p className="mt-0.5 break-words text-xs font-normal leading-4 text-slate-600">
                                {getDetailPreferenceText(detail)}
                              </p>
                            )}
                          </div>
                          <p className="text-center text-sm font-semibold leading-5 text-slate-900">
                            {detail?.quantity || 0}
                          </p>
                          <p className="truncate text-center text-sm leading-5 text-slate-700">
                            {getDetailNote(detail) || "--"}
                          </p>
                          {hasTaxColumn && (
                            <p className="text-center text-sm leading-5 text-slate-900">
                              {formatMoney(getDetailTaxTotal(detail))}
                            </p>
                          )}
                          <p className="text-right text-sm font-semibold leading-5 text-slate-950">
                            {formatMoney(detail?.totalValue)}
                          </p>
                        </div>
                        {getDetailModifierGroups(detail).map((group: any, groupIndex: number) => (
                          <div
                            key={`${group.title}-${groupIndex}`}
                            className="mt-2 flex w-full flex-wrap items-start gap-2 border-t border-slate-100 pt-2"
                          >
                            <p className="mt-1 min-w-[72px] shrink-0 text-[11px] font-bold leading-4 text-slate-600">
                              {group.title}
                            </p>
                            <div className="min-w-0 flex-1">
                              {renderModifierChips(
                                group.detail,
                                group.modifiers,
                                group.showPizzaControls,
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
                </div>
              );
            })()}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default AllOrders;
